export type TAggregationType = 'min' | 'max' | 'avg' | 'median';

export class AggregationsContainer {
  private _values: number[] = [];
  private _maxValue: number = -Infinity;
  private _minValue: number = +Infinity;
  private _avgValue: number = 0;
  private _count: number = 0;

  private readonly _isMaxEnabled: boolean = false;
  private readonly _isMinEnabled: boolean = false;
  private readonly _isAvgEnabled: boolean = false;
  private readonly _isMedianEnabled: boolean = false;

  constructor(aggregationTypes: TAggregationType[]) {
    if (aggregationTypes.includes('median')) {
      this._isMedianEnabled = true;
    }

    if (aggregationTypes.includes('min')) {
      this._isMinEnabled = true;
    }

    if (aggregationTypes.includes('max')) {
      this._isMaxEnabled = true;
    }

    if (aggregationTypes.includes('avg')) {
      this._isAvgEnabled = true;
    }
  }

  public addValue(value: number): void {
    if (this._isMedianEnabled) {
      this._values.push(value);
    }

    if (this._isMaxEnabled) {
      this._addMaxValue(value);
    }

    if (this._isMinEnabled) {
      this._addMinValue(value);
    }

    if (this._isAvgEnabled) {
      this._addAvgValue(value);
    }
  }

  public addValues(values: number[]): void {
    values.forEach((value) => {
      this.addValue(value);
    });
  }

  public clear(): void {
    this._values = [];
    this._maxValue = -Infinity;
    this._minValue = +Infinity;
    this._avgValue = 0;
    this._count = 0;
  }

  public clearMedian(): void {
    this._values = [];
  }

  public clearMax(): void {
    this._maxValue = -Infinity;
  }

  public clearMin(): void {
    this._minValue = -Infinity;
  }

  private _addMaxValue(value: number): void {
    if (value > this._maxValue) {
      this._maxValue = value;
    }
  }

  private _addMinValue(value: number): void {
    if (value < this._minValue) {
      this._minValue = value;
    }
  }

  private _addAvgValue(value: number): void {
    this._count++;
    this._avgValue += (value - this._avgValue) / this._count;
  }

  get max(): number | null {
    return this._maxValue > -Infinity ? this._maxValue : null;
  }

  get min(): number | null {
    return this._minValue < +Infinity ? this._minValue : null;
  }

  get avg(): number | null {
    return this._count ? this._avgValue : null;
  }

  get median(): number | null {
    if (!this._values.length) {
      return null;
    }

    const sortedValues: number[] = [...this._values].sort((a, b) => a - b);
    const middle: number = Math.floor(sortedValues.length / 2);

    return sortedValues.length % 2 === 0
      ? (sortedValues[middle - 1] + sortedValues[middle]) / 2
      : sortedValues[middle];
  }
}
